import type { SVGProps } from 'react'

/** Ultra-light line icons — one family, 1.6 stroke, round caps. */
function base(props: SVGProps<SVGSVGElement>) {
  return {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    width: 22,
    height: 22,
    'aria-hidden': true,
    ...props,
  } as const
}

export const IconPlan = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <circle cx="5.5" cy="18.5" r="2.2" />
    <circle cx="18.5" cy="5.5" r="2.2" />
    <path d="M7.2 17.2c3.4-1.4 2.6-4.4 5-6.4 1.6-1.3 3.5-1.6 4.8-3.2" />
  </svg>
)

export const IconTours = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M12 3.5 4 7.5l8 4 8-4-8-4Z" />
    <path d="m4 12 8 4 8-4" />
    <path d="m4 16.5 8 4 8-4" />
  </svg>
)

export const IconDiscover = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="m15.5 8.5-2.2 5-4.8 2 2.2-5 4.8-2Z" />
  </svg>
)

export const IconProfile = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <circle cx="12" cy="8.2" r="3.4" />
    <path d="M5.2 19.5c1.2-3.2 3.8-4.8 6.8-4.8s5.6 1.6 6.8 4.8" />
  </svg>
)

/* sports — recognizable pictograms, same 1.6 line weight */
export const IconHike = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="m2.8 19 5.4-8.8 3.4 5.2 3.9-6 5.7 9.6" />
    <path d="M15.5 9.4V3.6" />
    <path d="M15.5 3.6 19 5l-3.5 1.4" />
  </svg>
)

/* cycling icons: shared wheels, DISTINCT rider silhouettes per discipline */
const Wheels = () => (
  <>
    <circle cx="6" cy="17.6" r="3.1" />
    <circle cx="18" cy="17.6" r="3.1" />
  </>
)

/** upright rider + rear panniers = touring */
export const IconBike = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <Wheels />
    <path d="M6 17.6h5.3l-1.6-5.4h5.2M11.3 17.6l-1.6-5.4M14.9 12.2l1.6 5.4" />
    <circle cx="11" cy="4.2" r="1.7" />
    <path d="M10.8 6.1 9.7 12.2M10.6 7.4l4.6 1.4 .9 3.4" />
    <rect x="3.2" y="12.6" width="3.2" height="3.4" rx="0.9" />
  </svg>
)

/** standing rider, jagged summit line = MTB */
export const IconMtb = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <Wheels />
    <path d="M6 17.6h5.3l-1.4-5 4.8-.4M11.3 17.6l-1.4-5M15.3 12.2l1.4 5.4" />
    <circle cx="12.4" cy="4.6" r="1.7" />
    <path d="M12.1 6.4 10 10.2l3 2 -.6 3M11.8 7.5l4 1.6" />
    <path d="m2.6 6.2 2-2.6 1.6 2 2.2-2.9" opacity=".5" />
  </svg>
)

/** crouched aero rider on drop bars = road */
export const IconRoad = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <Wheels />
    <path d="M6 17.6h5.3l-1.5-5.2h5.4M11.3 17.6l-1.5-5.2M15.2 12.4l1.5 5.2M16.4 12.4v-1.6a1.7 1.7 0 0 0-1.7-1.7" />
    <circle cx="16.6" cy="5.4" r="1.7" />
    <path d="M15 6.3 9.8 8.6l-.2 3.8M14.8 7l1.7 3.2" />
    <path d="M2.4 7.4h3M2 10.4h2.5" opacity=".4" />
  </svg>
)

/** upright-ish rider + flying gravel dots = gravel */
export const IconGravel = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <Wheels />
    <path d="M6 17.6h5.3l-1.5-5.2h5.4M11.3 17.6l-1.5-5.2M15.2 12.4l1.5 5.2M16.4 12.4v-1.9a1.6 1.6 0 0 0-1.6-1.6" />
    <circle cx="13.9" cy="4.8" r="1.7" />
    <path d="M13.2 6.4 9.8 9l0 3.4M13 7.3l2.6 2.4" />
    <path d="M2.2 19.9h.01M4 22h.01M7.4 21.6h.01M11.6 21.9h.01M15.4 21.7h.01" strokeWidth="2" opacity=".5" />
  </svg>
)

export const IconRun = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <circle cx="14.9" cy="4.7" r="1.9" />
    <path d="M14.2 7.2 11.9 12" />
    <path d="m13.7 8.5 3.4 1.6M13.9 8.2l-3.4.7" />
    <path d="m11.9 12 3.2 2.5-1 4.6M11.9 12l-2.4 1.7-2.6-.9" />
    <path d="M4 6.8h3.2M3 10h2.6" opacity=".4" />
  </svg>
)

/* actions */
export const IconUndo = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M7.5 5.5 4 9l3.5 3.5" />
    <path d="M4 9h9a6 6 0 0 1 0 12h-2" />
  </svg>
)

export const IconSwap = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M16 4.5 19.5 8 16 11.5M19.5 8h-15" />
    <path d="M8 12.5 4.5 16 8 19.5M4.5 16h15" />
  </svg>
)

export const IconLoop = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M12 4a8 8 0 1 1-7.5 5.2" />
    <path d="M4 4.5v5h5" />
  </svg>
)

export const IconDownload = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M12 4v10.5M7.5 11 12 15.5 16.5 11" />
    <path d="M4.5 19.5h15" />
  </svg>
)

export const IconShare = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M12 14V4M8.5 7 12 3.5 15.5 7" />
    <path d="M6 11v8.5h12V11" />
  </svg>
)

export const IconNavArrow = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M12 3.5 19 20l-7-4.2L5 20l7-16.5Z" />
  </svg>
)

export const IconLayers = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <rect x="4" y="4" width="16" height="16" rx="4" />
    <path d="M4 12h16M12 4v16" opacity=".4" />
  </svg>
)

export const IconMountain3d = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="m3.5 17 5-8.5 3 4.5 3-5 6 9" />
    <path d="M3.5 20.5h17" opacity=".4" />
  </svg>
)

export const IconPlus = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M12 5v14M5 12h14" />
  </svg>
)

export const IconClose = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="m6 6 12 12M18 6 6 18" />
  </svg>
)

export const IconSearch = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <circle cx="10.8" cy="10.8" r="6.3" />
    <path d="m15.5 15.5 4.5 4.5" />
  </svg>
)

export const IconRecord = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="7.5" />
    <circle cx="12" cy="12" r="2.6" fill="currentColor" stroke="none" />
  </svg>
)

export const IconPause = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M9 6v12M15 6v12" strokeWidth="2" />
  </svg>
)

export const IconPlay = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M8 5.5v13l10-6.5-10-6.5Z" />
  </svg>
)

export const IconStop = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <rect x="7" y="7" width="10" height="10" rx="2" />
  </svg>
)

export const IconVoiceOn = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M4 10v4h3l4.5 4V6L7 10H4Z" />
    <path d="M15 9.5a4 4 0 0 1 0 5M17.5 7a8 8 0 0 1 0 10" opacity=".6" />
  </svg>
)

export const IconVoiceOff = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M4 10v4h3l4.5 4V6L7 10H4Z" />
    <path d="m15.5 9.5 5 5M20.5 9.5l-5 5" />
  </svg>
)

export const IconBack = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M14.5 5.5 8 12l6.5 6.5" />
  </svg>
)

export const IconTrash = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M5 7h14M9.5 7V4.8h5V7M7 7l1 13h8l1-13" />
  </svg>
)

export const IconWeather = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M8 17.5A4.5 4.5 0 1 1 9.7 9a5.5 5.5 0 0 1 10.6 1.6A3.5 3.5 0 0 1 19 17.5H8Z" />
  </svg>
)

export const IconGpx = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="M7 3.5h7L18.5 8v12.5h-11V3.5Z" />
    <path d="M14 3.5V8h4.5" />
    <path d="M9.5 15.5c1.8 0 1.5-3 3-3s1.2 3 3 3" opacity=".7" />
  </svg>
)

export const IconLocate = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
    <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
  </svg>
)

export const IconCheck = (p: SVGProps<SVGSVGElement>) => (
  <svg {...base(p)}>
    <path d="m5 12.5 4.5 4.5L19 7.5" />
  </svg>
)
